import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import { Activity, TriangleAlert } from "lucide-react";
import { AnalyticsPanels } from "@/components/dashboard/AnalyticsPanels";
import { BusinessAnalytics } from "@/components/dashboard/BusinessAnalytics";
import { FleetHealthStrip } from "@/components/dashboard/FleetHealthStrip";
import { KPICard } from "@/components/dashboard/KPICard";
import { HelpSection } from "@/components/HelpSection";
import { useCurrentUser } from "@/lib/auth";
import { useUnreadCount } from "@/lib/inbox";
import {
  type AccountHealth,
  type CountByKey,
  type RecentCampaign,
  type RecentScrapeJob,
  type StatsOverview,
  useAccountHealth,
  useRecentActivity,
  useStatsOverview,
} from "@/lib/stats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/table";
import { ProgressBar } from "@/components/ui/progress";
import { StatusBadge, Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRelative, formatDateTime, appLocale } from "@/lib/format";
import { cn } from "@/lib/cn";

type Tab = "overview" | "analytics";

/**
 * Navigate using the same mechanism as App.tsx's hand-rolled router
 * (pushState + a synthetic popstate). Used for critical-KPI click-through to
 * the (filtered) Accounts page until the router migration lands.
 */
function navigate(to: string) {
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function countFor(rows: CountByKey[], key: string): number {
  return rows.find((r) => r.key === key)?.count ?? 0;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const overview = useStatsOverview();
  const health = useAccountHealth();
  const activity = useRecentActivity();
  const [tab, setTab] = useState<Tab>("overview");
  if (!user) return null;

  const o = overview.data;
  const activeAccounts = o ? countFor(o.accounts.by_status, "active") : null;
  const contextStrip = [
    new Date().toLocaleDateString(appLocale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
    activeAccounts !== null
      ? t("dashboard.context.active_accounts", { count: activeAccounts })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <TooltipProvider delayDuration={300}>
      <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        <PageHeader
          title={t("dashboard.title")}
          subtitle={contextStrip}
          tabs={
            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
              <TabsList>
                <TabsTrigger value="overview">{t("dashboard.tab.overview")}</TabsTrigger>
                <TabsTrigger value="analytics">{t("dashboard.tab.analytics")}</TabsTrigger>
              </TabsList>
            </Tabs>
          }
        />

        {/* Tabs body — render contents conditionally so the Analytics query is
            only mounted (and fetched) when its tab is active. */}
        {tab === "overview" ? (
          <OverviewBody
            overview={overview.data}
            loadingOverview={overview.isLoading}
            health={health.data}
            loadingHealth={health.isLoading}
            scrapeJobs={activity.data?.scrape_jobs}
            campaigns={activity.data?.campaigns}
          />
        ) : (
          <div className="space-y-8">
            <BusinessAnalytics />
            {overview.data && (
              <LastOnlineDistribution
                kinds={overview.data.scraped_users.by_last_online_kind}
              />
            )}
            <AnalyticsPanels />
          </div>
        )}
      </section>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewBody({
  overview,
  loadingOverview,
  health,
  loadingHealth,
  scrapeJobs,
  campaigns,
}: {
  overview: StatsOverview | undefined;
  loadingOverview: boolean;
  health: AccountHealth[] | undefined;
  loadingHealth: boolean;
  scrapeJobs: RecentScrapeJob[] | undefined;
  campaigns: RecentCampaign[] | undefined;
}) {
  return (
    <div className="space-y-8">
      {overview && <NeedsAttention overview={overview} />}
      <FollowUpsDue />

      <FleetHealthStrip />

      {loadingOverview && !overview ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        overview && (
          <>
            <StrategyBreakdown overview={overview} />
            <WorkSummary overview={overview} />
            <RecentActivity scrapeJobs={scrapeJobs} campaigns={campaigns} />
            <AccountHealthTable health={health} loading={loadingHealth} />
          </>
        )
      )}

      <HelpSection i18nKey="dashboard.help" paragraphs={3} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs attention — the only red on the page, and only when it is earned
// ---------------------------------------------------------------------------

/**
 * Conditions an operator has to act on. Renders nothing when there are none:
 * an empty area here means "nothing is broken", which is the answer the
 * dashboard exists to give first.
 *
 * Deliberately NOT mirrored in FleetHealthStrip — the strip carries the green
 * "work is happening" numbers, this block owns the red ones. Duplicating them
 * is what made the old dashboard read as noise.
 */
function NeedsAttention({ overview }: { overview: StatsOverview }) {
  const { t } = useTranslation();
  const issues = [
    {
      key: "peerflood",
      count: overview.accounts.permanent_peer_flood,
      to: "/accounts",
    },
    {
      key: "banned",
      count: overview.accounts.banned,
      to: "/accounts?status=banned",
    },
    // Stored as banned, shown apart: a frozen account still reads, and the
    // list files it under its own status.
    {
      key: "frozen",
      count: overview.accounts.frozen,
      to: "/accounts?status=frozen",
    },
    // Spam-limited accounts look healthy everywhere else (they scrape and warm
    // up fine) but silently drop out of every outreach pool, so the usable
    // fleet quietly shrinks. Only the dashboard can surface that.
    {
      key: "spam_limited",
      count: overview.accounts.spam_limited,
      to: "/accounts?spam=limited",
    },
    {
      key: "campaigns_failed",
      // An auto-stopped campaign is "paused" with a reason, never "failed".
      count: overview.invites.stopped_with_error ?? 0,
      to: "/inviter",
    },
    { key: "proxies_dead", count: overview.proxies.dead, to: "/proxies" },
  ].filter((i) => i.count > 0);

  if (issues.length === 0) return null;

  return (
    <Alert tone="danger" title={t("dashboard.attention.title")}>
      <ul className="mt-1 space-y-1.5">
        {issues.map((i) => (
          <li key={i.key} className="flex flex-wrap items-baseline gap-x-2">
            <span>{t(`dashboard.attention.${i.key}`, { count: i.count })}</span>
            <button
              type="button"
              onClick={() => navigate(i.to)}
              className="text-sm font-medium underline-offset-4 hover:underline"
            >
              {t("dashboard.attention.open")}
            </button>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * Leads put off until a day that has come. A reminder, not a breakage — so it
 * is amber and stands apart from the red block, which stays for what is broken.
 * The number is the menu badge's own: no request of its own.
 */
function FollowUpsDue() {
  const { t } = useTranslation();
  const due = useUnreadCount().data?.follow_ups_due ?? 0;
  if (due === 0) return null;
  return (
    <Alert tone="warning">
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span>{t("dashboard.attention.follow_ups_due", { count: due })}</span>
        <button
          type="button"
          onClick={() => navigate("/inbox?status=due")}
          className="text-sm font-medium underline-offset-4 hover:underline"
        >
          {t("dashboard.attention.open")}
        </button>
      </p>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Work summary — what the fleet has produced, in six numbers
// ---------------------------------------------------------------------------

function WorkSummary({ overview }: { overview: StatsOverview }) {
  const { t } = useTranslation();
  return (
    <Section title={t("dashboard.summary.title")}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KPICard label={t("dashboard.kpi.channels")} value={overview.channels.total} />
        <KPICard
          label={t("dashboard.kpi.scraped_users")}
          value={overview.scraped_users.total}
          hint={t("dashboard.summary.with_username", {
            count: overview.scraped_users.with_username,
          })}
        />
        {/* "Доставлено", not "добавлено": DM / forward / story campaigns write
            the same `succeeded` column, and only manual_add literally adds
            anyone to a group. */}
        <KPICard
          label={t("dashboard.kpi.delivered")}
          value={overview.invites.cumulative_added}
          tone="success"
          hint={t("dashboard.summary.of_attempts", {
            count: overview.invites.cumulative_attempts,
          })}
        />
        <KPICard
          label={t("dashboard.kpi.failed")}
          value={overview.invites.cumulative_failed}
          tone={overview.invites.cumulative_failed > 0 ? "warning" : "neutral"}
        />
        {/* Replies are the only outcome a cold-DM campaign has that isn't
            "the message left the account". */}
        <KPICard
          label={t("dashboard.kpi.replies")}
          value={overview.inbox.incoming_7d}
          tone={overview.inbox.unread > 0 ? "accent" : undefined}
          hint={t("dashboard.summary.unread", { count: overview.inbox.unread })}
          onClick={() => navigate("/inbox")}
          ariaLabel={t("dashboard.kpi.replies")}
        />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Per-strategy breakdown — the fleet does four different jobs and only one of
// them is literally an "invite"
// ---------------------------------------------------------------------------

/** Low-to-high outreach pressure, same order the campaign wizard uses.
 * Unknown/new strategies sort last. */
const STRATEGY_ORDER = ["manual_add", "dm_message", "channel_forward", "story_mention"];

function StrategyBreakdown({ overview }: { overview: StatsOverview }) {
  const { t } = useTranslation();
  const rows = [...overview.invites.by_strategy]
    .filter((s) => s.campaigns > 0)
    .sort((a, b) => {
      const ia = STRATEGY_ORDER.indexOf(a.strategy);
      const ib = STRATEGY_ORDER.indexOf(b.strategy);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  if (rows.length === 0) {
    return (
      <Section title={t("dashboard.strategies.title")}>
        <Card className="px-4 py-3 text-sm text-fg-muted">
          {t("dashboard.strategies.empty")}
        </Card>
      </Section>
    );
  }

  return (
    <Section title={t("dashboard.strategies.title")}>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="border-b border-border bg-bg-secondary text-xs text-fg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {t("dashboard.strategies.col_strategy")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_campaigns")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_attempted")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_succeeded")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_people")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_failed")}
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {t("dashboard.strategies.col_rate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rate = r.attempted > 0 ? (r.succeeded / r.attempted) * 100 : null;
                return (
                  <tr
                    key={r.strategy}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-bg-elevated/40"
                    onClick={() => navigate("/inviter")}
                  >
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Badge tone="info" variant="subtle">
                          {t(`inviter.redesign.strategy_short.${r.strategy}`, {
                            defaultValue: r.strategy,
                          })}
                        </Badge>
                        <span className="text-xs text-fg-muted">
                          {t(`dashboard.strategies.outcome.${r.strategy}`, {
                            defaultValue: t("dashboard.strategies.outcome.default"),
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums">
                      {r.campaigns}
                      {r.active > 0 && (
                        <span className="ml-1 font-sans text-xs text-accent">
                          {t("dashboard.strategies.active_suffix", { count: r.active })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-fg-secondary">
                      {r.attempted}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-success">
                      {r.succeeded}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {r.people_reached}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                      {r.failed || "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {rate == null ? "—" : `${rate.toFixed(0)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 1 && (
              <tfoot className="border-t border-border bg-bg-secondary/60 text-sm">
                <tr>
                  <td className="px-3 py-2 font-medium">
                    {t("dashboard.strategies.total")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {rows.reduce((a, r) => a + r.campaigns, 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-fg-secondary">
                    {rows.reduce((a, r) => a + r.attempted, 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-success">
                    {rows.reduce((a, r) => a + r.succeeded, 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {rows.reduce((a, r) => a + r.people_reached, 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                    {rows.reduce((a, r) => a + r.failed, 0) || "—"}
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-fg-muted">
        {title}
      </h2>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Last-online distribution — stacked bar via ProgressBar segments
// ---------------------------------------------------------------------------

const KIND_FILL: Record<string, string> = {
  online: "bg-success",
  offline: "bg-success/70",
  recently: "bg-success/40",
  last_week: "bg-fg-muted",
  last_month: "bg-warning/60",
  long_ago: "bg-danger/70",
  hidden: "bg-fg-muted/40",
  unknown: "bg-fg-muted/20",
};

const KIND_ORDER = [
  "online",
  "offline",
  "recently",
  "last_week",
  "last_month",
  "long_ago",
  "hidden",
  "unknown",
];

function LastOnlineDistribution({ kinds }: { kinds: CountByKey[] }) {
  const { t } = useTranslation();
  if (kinds.length === 0) return null;
  const total = kinds.reduce((s, k) => s + k.count, 0);
  if (total === 0) return null;
  const sorted = [...kinds].sort(
    (a, b) => KIND_ORDER.indexOf(a.key) - KIND_ORDER.indexOf(b.key),
  );

  return (
    <Section title={t("dashboard.kpi.last_online_dist")}>
      <Card className="p-4">
        <div className="flex h-3 overflow-hidden rounded-full bg-bg-elevated">
          {sorted.map((k) => {
            const pct = (k.count / total) * 100;
            return (
              <Tooltip
                key={k.key}
                content={`${t(`users.last_seen.${k.key}`, {
                  defaultValue: t("users.last_seen.unknown"),
                })}: ${k.count} (${pct.toFixed(1)}%)`}
              >
                <div
                  style={{ width: `${pct}%` }}
                  className={cn("h-full", KIND_FILL[k.key] ?? "bg-fg-muted")}
                />
              </Tooltip>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
          {sorted.map((k) => (
            <span key={k.key} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn(
                  "h-2 w-2 rounded-full",
                  KIND_FILL[k.key] ?? "bg-fg-muted",
                )}
              />
              <span className="text-fg-muted">
                {t(`users.last_seen.${k.key}`, {
                  defaultValue: t("users.last_seen.unknown"),
                })}
              </span>
              <span className="font-mono tabular-nums">{k.count}</span>
            </span>
          ))}
        </div>
      </Card>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Account health — sortable DataTable, clickable rows → /accounts
// ---------------------------------------------------------------------------

/** How many rows the dashboard shows before handing off to /accounts. The
 * table used to render the whole fleet — 120 accounts made the page 7 000px
 * tall and buried everything under it. */
const HEALTH_ROWS = 8;

/** Sort so the rows worth looking at come first: dead, then throttled, then
 * everything else by most-recently-used. */
function healthSeverity(a: AccountHealth): number {
  if (a.status === "banned" || a.is_peer_flood_permanent) return 0;
  if (a.status === "auth_required") return 1;
  if (a.status === "flood_wait") return 2;
  if (a.last_check_error) return 3;
  return 4;
}

function AccountHealthTable({
  health,
  loading,
}: {
  health: AccountHealth[] | undefined;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const total = health?.length ?? 0;
  const rows = useMemo(() => {
    if (!health) return [];
    return [...health]
      .sort((a, b) => {
        const d = healthSeverity(a) - healthSeverity(b);
        if (d !== 0) return d;
        return (b.last_used_at ?? "").localeCompare(a.last_used_at ?? "");
      })
      .slice(0, HEALTH_ROWS);
  }, [health]);

  const columns = useMemo<ColumnDef<AccountHealth, unknown>[]>(
    () => [
      {
        id: "account",
        header: t("dashboard.health.account"),
        accessorFn: (a) => a.display_name,
        cell: ({ row }) => (
          <span className="font-medium text-fg-primary">
            {row.original.display_name}
          </span>
        ),
      },
      {
        id: "status",
        header: t("dashboard.health.status"),
        accessorFn: (a) => a.status,
        cell: ({ row }) => {
          const a = row.original;
          const isFW =
            a.flood_wait_until && new Date(a.flood_wait_until) > new Date();
          const badge = (
            <StatusBadge
              domain="account"
              value={a.status}
              label={t(`accounts.status.${a.status}`, { defaultValue: a.status })}
              dot
            />
          );
          if (isFW && a.flood_wait_until) {
            return (
              <Tooltip
                content={t("dashboard.health.flood_until", {
                  time: formatDateTime(a.flood_wait_until),
                })}
              >
                {badge}
              </Tooltip>
            );
          }
          return badge;
        },
      },
      {
        id: "role",
        header: t("dashboard.health.role"),
        accessorFn: (a) => a.role,
        cell: ({ row }) => {
          const a = row.original;
          const label = t(`accounts.role.${a.role}`, { defaultValue: a.role });
          if (a.is_peer_flood_permanent) {
            return (
              <Tooltip content={t("accounts.peer_flood_perm")}>
                <span className="inline-flex items-center gap-1.5">
                  {label}
                  <Badge tone="danger">PF</Badge>
                </span>
              </Tooltip>
            );
          }
          return <span>{label}</span>;
        },
      },
      {
        id: "proxy",
        header: t("dashboard.health.proxy"),
        accessorFn: (a) => a.proxy_label ?? "",
        cell: ({ row }) => (
          <span className="block max-w-[10rem] truncate whitespace-nowrap text-xs text-fg-muted">
            {row.original.proxy_label ?? "—"}
          </span>
        ),
      },
      {
        id: "invites",
        header: t("dashboard.health.sent"),
        accessorFn: (a) => a.total_invites_sent,
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.total_invites_sent}
          </span>
        ),
      },
      {
        id: "floodwaits",
        header: t("dashboard.health.floodwaits"),
        accessorFn: (a) => a.total_floodwaits,
        cell: ({ row }) => {
          const a = row.original;
          return (
            <span className="inline-flex items-center gap-1.5 font-mono tabular-nums">
              {a.total_floodwaits}
              {a.peer_flood_count > 0 && (
                <Tooltip content={t("dashboard.health.pf_count")}>
                  <span className="text-xs text-danger">PF×{a.peer_flood_count}</span>
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        id: "last_used",
        header: t("dashboard.health.last_used"),
        accessorFn: (a) => a.last_used_at ?? "",
        cell: ({ row }) => {
          const a = row.original;
          const rel = a.last_used_at ? formatRelative(a.last_used_at) : "—";
          if (a.last_check_error) {
            return (
              <Tooltip content={a.last_check_error}>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-fg-muted">
                  {rel}
                  <TriangleAlert
                    size={13}
                    strokeWidth={2}
                    aria-hidden="true"
                    className="text-warning"
                  />
                </span>
              </Tooltip>
            );
          }
          return <span className="whitespace-nowrap text-fg-muted">{rel}</span>;
        },
      },
    ],
    [t],
  );

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-fg-muted">
          {t("dashboard.health.title")}
        </h2>
        {total > 0 && (
          <button
            type="button"
            onClick={() => navigate("/accounts")}
            className="text-xs font-medium text-accent underline-offset-4 hover:underline"
          >
            {t("dashboard.health.see_all", { count: total })}
          </button>
        )}
      </div>
      {total > HEALTH_ROWS && (
        <p className="text-xs text-fg-muted">
          {t("dashboard.health.shown_hint", { shown: HEALTH_ROWS, total })}
        </p>
      )}
      {loading && !health ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(a) => a.id}
          onRowClick={() => navigate("/accounts")}
          empty={
            <EmptyState
              icon={Activity}
              title={t("dashboard.health.empty_title")}
              description={t("dashboard.health.empty_hint")}
            />
          }
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recent activity — clickable rows, mini progress bar, relative time
// ---------------------------------------------------------------------------

function RecentActivity({
  scrapeJobs,
  campaigns,
}: {
  scrapeJobs: RecentScrapeJob[] | undefined;
  campaigns: RecentCampaign[] | undefined;
}) {
  const { t } = useTranslation();
  if (!scrapeJobs && !campaigns) return null;

  return (
    <section className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("dashboard.activity.scrape_jobs")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!scrapeJobs || scrapeJobs.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t("dashboard.activity.empty")}
            </p>
          ) : (
            <ul className="space-y-1">
              {scrapeJobs.map((j) => (
                <li key={j.id}>
                  {/* Fixed grid columns instead of a flex row: badge widths
                      differ per status, and with flex the counters and
                      timestamps ended up ragged down the list. */}
                  <button
                    type="button"
                    onClick={() => navigate("/users")}
                    className="grid w-full grid-cols-[6.5rem_minmax(0,1fr)_5rem] items-center gap-x-2 gap-y-1 rounded-lg px-2 py-2 text-left transition-colors hover:bg-bg-elevated/40 sm:grid-cols-[minmax(0,1fr)_6.5rem_5rem_5rem]"
                  >
                    <Tooltip
                      content={
                        j.channel_username
                          ? `@${j.channel_username}`
                          : j.channel_title || "—"
                      }
                    >
                      <span className="col-span-full min-w-0 truncate text-sm sm:col-span-1">
                        {j.channel_username
                          ? `@${j.channel_username}`
                          : j.channel_title || "—"}
                      </span>
                    </Tooltip>
                    <span className="min-w-0">
                      <StatusBadge
                        domain="scrape_job"
                        value={j.status}
                        label={t(`channels.status.${j.status}`, {
                          defaultValue: j.status,
                        })}
                      />
                    </span>
                    <span className="text-right font-mono text-xs tabular-nums text-fg-muted">
                      {j.users_added}/{j.users_seen}
                    </span>
                    <span className="truncate text-right text-xs text-fg-muted">
                      {j.finished_at ? formatRelative(j.finished_at) : "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("dashboard.activity.campaigns")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!campaigns || campaigns.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t("dashboard.activity.empty")}
            </p>
          ) : (
            <ul className="space-y-1">
              {campaigns.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => navigate("/inviter")}
                    className="flex w-full flex-col gap-1.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-bg-elevated/40"
                  >
                    <div className="grid w-full grid-cols-[5.5rem_6rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1 sm:grid-cols-[minmax(0,1fr)_5.5rem_6rem_4.5rem]">
                      <Tooltip content={c.name}>
                        <span className="col-span-full min-w-0 truncate text-sm font-medium sm:col-span-1">
                          {c.name}
                        </span>
                      </Tooltip>
                      {/* Which of the four jobs this campaign is doing — a DM
                          blast and an add-to-group read identically without it. */}
                      <span className="min-w-0">
                        <Badge tone="info" variant="subtle">
                          {t(`inviter.redesign.strategy_short.${c.strategy}`, {
                            defaultValue: c.strategy,
                          })}
                        </Badge>
                      </span>
                      <span className="min-w-0">
                        <StatusBadge
                          domain="campaign"
                          value={c.status}
                          label={t(`inviter.status.${c.status}`, {
                            defaultValue: c.status,
                          })}
                        />
                      </span>
                      <span className="text-right font-mono text-xs tabular-nums text-fg-muted">
                        {c.succeeded}/{c.total_targets}
                      </span>
                    </div>
                    <ProgressBar
                      compact
                      value={c.succeeded}
                      max={c.total_targets || 1}
                      tone="success"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
